import { growthFactor, type Timing } from "@/lib/tool-kit/finance/tvm";

/** Discount-factor tables for the present value calculator. */

export interface DiscountRow {
  period: number;
  factor: number;
  cashFlow: number;
  present: number;
  /** Sum of the present values listed so far. */
  running: number;
}

/**
 * The first `count` payments of an annuity or growing perpetuity, each discounted at the
 * rate `i` per period. Payment k is `payment × (1+g)^(k−1)`; it falls at the end of
 * period k, or at the start (time k−1) for an annuity due.
 */
export function cashFlowSchedule(input: {
  payment: number;
  growth: number;
  i: number;
  count: number;
  timing: Timing;
}): DiscountRow[] {
  const { payment, growth, i, count, timing } = input;
  const rows: DiscountRow[] = [];
  let running = 0;
  for (let k = 1; k <= count; k += 1) {
    const time = timing === "begin" ? k - 1 : k;
    const factor = (1 + i) ** -time;
    const cashFlow = payment * (1 + growth) ** (k - 1);
    const present = cashFlow * factor;
    running += present;
    rows.push({ period: k, factor, cashFlow, present, running });
  }
  return rows;
}

/** The discount factor at the end of each whole year, to `maxYears`. */
export function yearlyFactors(
  annualRate: number,
  perYear: number,
  years: number,
  maxYears = 40
): { year: number; factor: number }[] {
  const rows: { year: number; factor: number }[] = [];
  const whole = Math.min(Math.floor(years), maxYears);
  for (let year = 1; year <= whole; year += 1) {
    rows.push({ year, factor: 1 / growthFactor(annualRate, perYear, year) });
  }
  if (years - whole > 1e-9 && whole < maxYears) {
    rows.push({
      year: years,
      factor: 1 / growthFactor(annualRate, perYear, years),
    });
  }
  return rows;
}
